import "./App.scss";
import "@altimateai/lego/styles/globals.css";
import "@altimateai/ui-components/styles.css";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getTenant } from "./helpers/tenant";
import { getUser } from "./services/auth";
import { initializeSuperTokens, SupertokensProvider } from "./supertokens";
import * as Sentry from "@sentry/react";
import { ENV, SENTRY_DSN, ENABLE_SENTRY_ERRORS } from "./config";
import { Suspense, useEffect } from "react";
import { Provider as RollbarProvider, ErrorBoundary as RollbarErrorBoundary } from "@rollbar/react";
import { initializeRollbar } from "./rollbarConfig";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { setTenantHeader } from "@/helpers/api_helper";
import AppProvider from "./modules/app/AppProvider";
import PlaygroundProvider from "./modules/playground/PlaygroundProvider";
import { TeamMateProvider } from "@altimateai/ui-components/teammate";
import ColumnPickerProvider from "./components/column/ColumnPickerProvider";
import CookieConsentBanner from "./components/cookieConsentBanner/CookieConsentBanner";
import { RelativeComponentLoader } from "./components/loader";
import ErrorBoundaryWithReset from "./components/error/ErrorBoundaryWithReset";

import "./dayjsConfig";
import SidebarResizeListener from "./EventListeners";
import { Toaster, ToastProvider, ToastViewport, TooltipProvider } from "@altimateai/lego";
import AppRoutes from "./route/Routes";
import { InsightContextProvider } from "./modules/insights/context";
import { DemoTokenHandler } from "./components/DemoTokenHandler";

initializeSuperTokens();
initializeRollbar();

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // Captures all api errors, will noisy initially ex: login but we could filter out later
    onError: error => {
      Sentry.captureException(error);
      // Also send to Rollbar if enabled
      if (window.Rollbar) {
        window.Rollbar.error(error as Error);
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

if (ENABLE_SENTRY_ERRORS) {
  const user = getUser();
  Sentry.init({
    environment: ENV,
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    initialScope: {
      tags: {
        tenant: getTenant(),
      },
      user: user
        ? {
            email: user.email,
          }
        : undefined,
    },
  });
}

function App() {
  const tenant = getTenant();
  setTenantHeader(tenant);

  const appContent = (
    <div className="App">
      <ToastProvider>
        <TooltipProvider>
          <SupertokensProvider>
            <BrowserRouter basename={window.__BASENAME__ || ""}>
              <AppProvider>
                <SidebarResizeListener />
                <DemoTokenHandler>
                  <QueryClientProvider client={queryClient}>
                    <PlaygroundProvider>
                      <ErrorBoundaryWithReset>
                        <TeamMateProvider>
                          <InsightContextProvider>
                            <ColumnPickerProvider>
                              <Toaster />
                              <ToastViewport />
                              <Suspense fallback={<RelativeComponentLoader label="Loading..." />}>
                                <AppRoutes />
                              </Suspense>
                            </ColumnPickerProvider>
                          </InsightContextProvider>
                        </TeamMateProvider>
                      </ErrorBoundaryWithReset>
                    </PlaygroundProvider>
                  </QueryClientProvider>
                </DemoTokenHandler>
                <CookieConsentBanner />
              </AppProvider>
            </BrowserRouter>
          </SupertokensProvider>
        </TooltipProvider>
      </ToastProvider>
    </div>
  );

  // Wrap with Rollbar if enabled
  if (window.Rollbar) {
    return (
      <RollbarProvider instance={window.Rollbar}>
        <RollbarErrorBoundary>{appContent}</RollbarErrorBoundary>
      </RollbarProvider>
    );
  }

  return appContent;
}

export default App;
